import React from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { useCreateLifecycleProspectMutation } from '@edx/graphql/generated';
import { useAppConfigStateContext } from '@edx/contexts/app-config';
import { useNotification } from '@edx/hooks/use-notification';
import { useTrackedCampaigns } from '@edx/hooks/use-track-campaigns';
import { useValidationErrors } from '@edx/hooks/use-validation-errors';
import { Button } from '@edx/components/button';
import { TextInput } from '@edx/components/form';
import { IconPlaySolid } from '@edx/components/icons';
import { Body, Intro, Title } from '@edx/components/typography';

type Props = {
  videoId: string;
  thumbnail: {
    src: string;
    alt: string;
  };
};

export const ProspectLifecycle: React.FC<Props> = ({ videoId, thumbnail }) => {
  const router = useRouter();
  const { projectShortName } = useAppConfigStateContext();
  const campaign = useTrackedCampaigns(`${projectShortName}::campaign`);
  const { showNotification: showSuccessNotification } = useNotification();
  const { showNotification: showErrorNotification } = useNotification('error');
  const [email, setEmail] = React.useState('');
  const [onCreateLifecycle, { loading: isSubmitting, data, error }] = useCreateLifecycleProspectMutation({
    variables: { input: { email, videoId, campaign } },
  });
  const { errors: validationErrors } = useValidationErrors(error?.graphQLErrors);

  const handleInput = (setter: React.Dispatch<React.SetStateAction<string>>) => (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => setter(event.target.value);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onCreateLifecycle()
      .then((response) => {
        const { id, incentiveUrl, isPendingConfirmation } = response?.data?.createLifecycleProspect || {};

        if (!id) showErrorNotification({ title: '¡Ups!', message: 'Se ha producido un error, inténtalo de nuevo.' });
        else if (incentiveUrl) router.push(incentiveUrl);
        else if (isPendingConfirmation)
          showSuccessNotification({
            title: '¡Gracias por apuntarte!',
            message: 'Por favor, confirma tu email.',
          });
      })
      .catch(() => showErrorNotification({ title: '¡Ups!', message: 'Se ha producido un error, inténtalo de nuevo.' }));
  };

  const didOptIn = Boolean(data?.createLifecycleProspect.id);

  return (
    <section className="relative">
      <div className="hidden md:block absolute inset-0">
        <picture>
          <source media="(min-width: 1122px)" srcSet="/img/dashboard/dashboard_heading-l.jpg" />
          <source media="(min-width: 641px)" srcSet="/img/dashboard/dashboard_heading-m.jpg" />
          <img className="h-full w-full object-cover object-top" src="/img/dashboard/dashboard_heading-s.jpg" alt="" />
        </picture>
      </div>

      <div className="relative max-w-6xl mx-auto flex flex-col-reverse md:flex-row justify-between space-x-0 md:space-x-4 py-32 px-4 md:px-6 lg:px-10">
        <div className="w-full md:w-1/2 flex flex-col mt-4 md:mt-0">
          <span className="w-8 h-1 bg-primary-500" />

          <Intro tag="h2" size="small" className="md:text-intro text-gray-300 mt-1">
            {didOptIn ? 'Confirma tu email' : '¿Quieres ver este video gratis?'}
          </Intro>

          <Title tag="h1" className="text-gray-300 leading-tight mt-3">
            {didOptIn
              ? '¡Gracias por apuntarte! Por favor, confirma tu email'
              : 'Desbloquea éste y más contenidos apuntándote al curso gratuito "Salir del estancamiento en 30 días"'}
          </Title>

          {didOptIn ? (
            <div className="mt-3">
              <Body size="xlarge" className="text-gray-500 leading-tight">
                Te hemos enviado un email con un enlace para confirmar tu dirección. Una vez confirmes tu email, podrás
                ver el video inmediatamente.
              </Body>
              <Body size="xlarge" className="text-gray-500 leading-tight">
                Si no recibes la confirmación, mira en la carpeta de correo no deseado o spam.
              </Body>
            </div>
          ) : (
            <div className="mt-4">
              <Body size="xlarge" className="text-gray-500 leading-tight">
                Supera la sensación de tocar siempre lo mismo y utilizar los mismos recursos, con este curso exclusivo
                por email.
              </Body>

              <form className="flex mt-4" method="post" onSubmit={handleSubmit}>
                <div className="flex-grow">
                  <TextInput
                    variant="dark"
                    placeholder="Tu e-mail"
                    value={email}
                    error={validationErrors.email}
                    onChange={handleInput(setEmail)}
                    className="rounded-r-none"
                  />
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  textVariant="medium"
                  className="flex-shrink-0 rounded-l-none"
                  disabled={isSubmitting}
                >
                  Desbloquear video
                </Button>
              </form>
            </div>
          )}
        </div>

        <div className="w-full md:w-1/2 flex-shrink-0">
          <div className="relative border border-gray-600">
            <Image
              src={thumbnail.src}
              alt={thumbnail.alt}
              width={1120}
              height={630}
              layout="responsive"
              className="aspect-ratio-16/9"
            />

            {/* Overlay */}
            <div className="absolute inset-0 flex items-center space-y-2 justify-center bg-gray-800 bg-opacity-40 hover:bg-opacity-0 transition duration-300">
              <div className="cursor-pointer py-2 px-3 bg-gray-800 rounded flex items-center justify-center">
                <IconPlaySolid className="text-primary-500 w-5" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
